// import * as Next from 'next'
import * as React from 'react'
import { ParsedUrlQuery } from 'querystring'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import * as Types from '../../../../../declarations'
// import * as DataUtil from '../../../../lib/data'
import * as RedisUtil from 'lib/redis'
import { getTrekURL, getUploadThumbPath } from '../../../../../lib/display'
import PageTemplate from '../../../../../components/PageTemplate'

import FullTripPage from '../../../../../components/TrekPage/FullTripPage'
import { withVersion } from '../../../../../lib/withVersion'
import SuggestEditModal from 'components/SuggestEditModal'

interface TrekPageProps {
	trip: Types.DB.Models.TripAttributes
	version: Types.VERSION
}

const TrekProtoPage = ({ trip, version }: TrekPageProps) => {
	const { isFallback, query } = useRouter()
	const isAdSource = query.utm_medium === 'cpc'

	if (isFallback) {
		return <div>Loading...</div>
	}

	const seo = {
		title: `${version === Types.VERSION.TREKKING ? 'Trek' : 'Climb'} ${
			trip.name
		} - Everything you need to know for ${
			version === Types.VERSION.TREKKING ? 'Trekking' : 'Climbing'
		} ${trip.name} in 2025 - Guided ${trip.name} ${
			version === Types.VERSION.TREKKING ? 'Treks' : 'Expeditions'
		} - ${
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks'
		}`,
		description: `Everything you need to know about ${
			trip.jsonData.trekOrClimb === 'TREK' ? 'trekking' : 'climbing'
		} ${
			trip.name
		}: best season, gear checklist, costs, and accommodation tips. Plan your trip.`,
		canonical: getTrekURL(
			trip.jsonData.trekOrClimb,
			trip.jsonData.continent,
			trip.jsonData.countries,
			trip.slug,
			version,
		),
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
	}

	const firstPics = React.useMemo(
		() =>
			trip.uploads
				?.filter(({ isDisplayedInGallery }) => isDisplayedInGallery)
				.filter((item, index) => index < 5),
		[trip],
	)

	return (
		<>
			<Head>
				<title>{seo.title}</title>
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: firstPics?.map((image) => ({
						url: getUploadThumbPath(image.id, 'large'),
						width: +image.dimensionsLarge.split('x')[0],
						height: +image.dimensionsLarge.split('x')[1],
						alt: trip.name,
						type: 'image/jpeg',
					})),
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<PageTemplate>
				<SuggestEditModal version={version} resourceId={trip.uniqueTripId} />
				<FullTripPage trip={trip} version={version} isAdSource={isAdSource} />
			</PageTemplate>
		</>
	)
}

interface TrekPageQuery extends ParsedUrlQuery {
	routeType?: string
	slug?: string
}

export const getServerSideProps = withVersion(async (context) => {
	const { routeType, slug } = context.params as TrekPageQuery

	// todo: merge these two 404 checks in to one (route type and slug check), and maybe even the third - no trip found
	if (!(routeType === 'trek' || routeType === 'climbing')) {
		// 404?
		return {
			notFound: true,
		}
	}

	if (!slug) {
		// 404
		return {
			notFound: true,
		}
	}

	const trek = await RedisUtil.Data.TripPage.get(
		{
			slug,
			version: context.version,
		},
		// todo: comment this back out
		// false,
		// true,
	)

	if (!trek) {
		return {
			notFound: true,
		}
	}

	return {
		props: {
			trip: trek,
			version: context.version,
		},
	}
})

export default TrekProtoPage
